import type { RefundableOrderView, RefundView } from "@kopi/contracts";
import { DEFAULT_REFUND_SORT, REFUND_SORTS, type SortValue } from "@kopi/contracts/client";
import {
  REFUND_STATUSES,
  type RefundDisposition,
  type RefundMethod,
  type RefundStatus,
} from "@kopi/contracts/codes";
import { lineValue } from "@kopi/domain";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@kopi/ui/components/card";
import { DataTable, type DataTableColumn } from "@kopi/ui/components/data-table";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { RadioGroup, RadioGroupItem } from "@kopi/ui/components/radio-group";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { Textarea } from "@kopi/ui/components/textarea";
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { BadgeCheckIcon, BanIcon, SearchIcon, Undo2Icon } from "lucide-react";
import { type FormEvent, useId, useMemo, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import { refunds as refundsArea } from "@/i18n/messages/refunds";
import { useMessages } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { newId } from "@/lib/id";
import { useOnlineStatus } from "@/lib/online";
import { meScopesQuery, outletsQuery, PAGE } from "@/lib/queries";
import { sortParam, urlSorting } from "@/lib/sorting";
import { ReceiptActions } from "@/receipts/receipt-actions";

const ALL = "all";
const KEY = ["refunds"] as const;

const useRefundMessages = () => useMessages(refundsArea);
type RefundsT = ReturnType<typeof useRefundMessages>["t"];

type RefundSort = SortValue<(typeof REFUND_SORTS)[number]>;

/** The status filter and the column sort (R1-09d) live in the URL, so they survive a reload. */
function validateSearch(search: Record<string, unknown>): {
  status: RefundStatus | typeof ALL;
  sort?: RefundSort;
} {
  const raw = search.status;
  const status =
    raw === ALL || REFUND_STATUSES.includes(raw as RefundStatus) ? (raw as RefundStatus | typeof ALL) : ALL;
  const sort = sortParam(REFUND_SORTS, search.sort);
  return sort ? { status, sort } : { status };
}

const refundsQuery = (outletId: string, status: RefundStatus | undefined, sort?: RefundSort) =>
  infiniteQueryOptions({
    queryKey: [...KEY, "list", outletId, { status, sort }],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: { nextCursor: string | null }) => page.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.outlets({ outletId }).refunds.get({
          query: {
            limit: PAGE,
            ...(pageParam ? { cursor: pageParam } : {}),
            ...(status ? { status } : {}),
            ...(sort ? { sort } : {}),
          },
        }),
      ),
    enabled: Boolean(outletId),
  });

const orderQuery = (outletId: string, receipt: string) => ({
  queryKey: [...KEY, "order", outletId, receipt] as const,
  queryFn: async (): Promise<RefundableOrderView> =>
    unwrap(await api.v1.outlets({ outletId }).receipts({ receiptNumber: receipt }).refunds.get()),
  enabled: Boolean(outletId && receipt),
  retry: false,
});

export const Route = createFileRoute("/_authed/refunds")({
  staticData: { section: "workspace", crumb: "nav.refunds" },
  validateSearch,
  loader: ({ context }) => context.queryClient.ensureQueryData(outletsQuery),
  component: RefundsPage,
});

const STATUS_VARIANT: Record<RefundStatus, "success" | "warning" | "destructive" | "outline"> = {
  requested: "outline",
  authorized: "warning",
  processing: "warning",
  unknown: "warning",
  succeeded: "success",
  failed: "destructive",
};

function StatusBadge(props: { status: RefundStatus }) {
  const { t } = useRefundMessages();
  return <Badge variant={STATUS_VARIANT[props.status]}>{t(`refunds.status.${props.status}`)}</Badge>;
}

/** Why a refund command was refused, in the interface language. */
function failure(t: RefundsT, error: unknown): string {
  const code = error instanceof ApiRequestError ? error.code : null;
  switch (code) {
    case "refund_exceeds_balance":
      return t("refunds.error.exceeds");
    case "refund_quantity_exceeded":
      return t("refunds.error.quantity");
    case "restock_not_allowed":
      return t("refunds.error.restock");
    case "register_required":
      return t("refunds.error.register");
    case "no_active_shift":
    case "shift_not_open":
      return t("refunds.error.shift");
    case "transfer_reference_required":
      return t("refunds.error.transfer");
    case "gateway_refund_unavailable":
      return t("refunds.error.gateway");
    case "refund_revision_conflict":
    case "refund_not_requested":
      return t("refunds.error.changed");
    case "not_refundable":
      return t("refunds.error.notRefundable");
    default:
      return t("refunds.error.failed");
  }
}

const columnsFor = (t: RefundsT, money: (v: bigint) => string, when: (iso: string) => string) =>
  [
    {
      id: "refund",
      header: t("refunds.column.refund"),
      meta: { card: "title" },
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <span className="font-medium tabular-nums">
            {t("refunds.order", { number: row.original.receiptNumber ?? "—" })}
          </span>
          <span
            className="max-w-56 truncate text-muted-foreground text-sm md:max-w-72"
            title={row.original.reason}
          >
            {row.original.method ? `${t(`refunds.method.${row.original.method}`)} · ` : ""}
            {row.original.reason}
          </span>
        </div>
      ),
    },
    {
      id: "requested",
      header: t("refunds.column.requested"),
      enableSorting: true,
      cell: ({ row }) => (
        <span className="text-sm">
          {t("refunds.requestedBy", { name: row.original.requestedBy, time: when(row.original.requestedAt) })}
        </span>
      ),
    },
    {
      id: "status",
      header: t("refunds.status"),
      meta: { className: "md:w-36" },
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: "amount",
      header: t("refunds.column.amount"),
      enableSorting: true,
      meta: { align: "end" },
      cell: ({ row }) => <span className="tabular-nums">{money(BigInt(row.original.amount))}</span>,
    },
  ] satisfies DataTableColumn<RefundView>[];

/**
 * Refunds (R1-29, PRD PAY-05): find a paid order by its receipt number, then request a refund or —
 * with `refunds.approve` — authorize and pay it: cash from this register's drawer, a recorded manual
 * transfer, or the QRIS gateway where it offers refunds. Prepared items are wasted; an inspected,
 * unopened retail item may go back to stock. Below, the outlet's refunds, with requests to decide.
 */
function RefundsPage() {
  const { t, format } = useRefundMessages();
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const { outlet: current } = useCurrentOutlet();
  const usable = outlets.filter(
    (o) => canAt(scopes, "sales.create", o.id) || canAt(scopes, "refunds.approve", o.id),
  );
  const [outletId, setOutletId] = useState(
    usable.find((o) => o.id === current?.id)?.id ?? usable[0]?.id ?? "",
  );
  const outlet = usable.find((o) => o.id === outletId);
  const mayApprove = canAt(scopes, "refunds.approve", outletId);
  const [typed, setTyped] = useState("");
  const [receipt, setReceipt] = useState("");
  const found = useQuery(orderQuery(outletId, receipt));
  const [creating, setCreating] = useState(false);
  const [deciding, setDeciding] = useState<{ refund: RefundView; kind: "approve" | "decline" } | null>(null);
  const { status, sort } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const sorting = urlSorting(REFUND_SORTS, DEFAULT_REFUND_SORT, sort, (next) => {
    void navigate({ search: next ? { status, sort: next } : { status }, replace: true });
  });
  const list = useInfiniteQuery(refundsQuery(outletId, status === ALL ? undefined : status, sort));
  const columns = useMemo(
    () =>
      columnsFor(
        t,
        (v) => format.money(v),
        (iso) => format.dateTime(iso, outlet?.timezone),
      ),
    [t, format, outlet?.timezone],
  );
  const receiptId = useId();

  if (!outlet) {
    return (
      <Page>
        <PageHeader title={t("nav.refunds")} subtitle={t("refunds.noOutlet")} />
      </Page>
    );
  }
  const search = () => setReceipt(typed.replace(/\D/g, "").replace(/^0+(?=\d)/, ""));
  const order = found.data;
  return (
    <Page>
      <PageHeader title={t("nav.refunds")} subtitle={t("refunds.subtitle")} />
      <div className="flex flex-col gap-3 md:flex-row md:items-end">
        {/* A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine). */}
        <OptionSelect
          aria-label={t("outlet.heading")}
          className="md:w-56"
          options={usable.map((o) => ({ value: o.id, label: o.name }))}
          value={outletId}
          onValueChange={(value) => {
            setOutletId(value);
            setReceipt("");
          }}
        />
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor={receiptId}>{t("refunds.receipt")}</Label>
          <div className="flex gap-2">
            <Input
              id={receiptId}
              inputMode="numeric"
              autoComplete="off"
              className="tabular-nums md:w-56"
              placeholder={t("refunds.receiptPlaceholder")}
              value={typed}
              onChange={(e) => setTyped(e.target.value.replace(/\D/g, "").slice(0, 9))}
              onKeyDown={(e) => {
                if (e.key === "Enter") search();
              }}
            />
            <Button type="button" variant="outline" disabled={typed === ""} onClick={search}>
              <SearchIcon aria-hidden />
              {t("refunds.find")}
            </Button>
          </div>
        </div>
      </div>
      {found.isError && (
        <p role="alert" className="text-destructive text-sm">
          {t("refunds.notFound")}
        </p>
      )}
      {order && <OrderCard order={order} onRefund={() => setCreating(true)} timeZone={outlet.timezone} />}
      <DataTable
        paging="pages"
        label={t("refunds.list")}
        columns={columns}
        query={list}
        getRowId={(r) => r.id}
        {...sorting}
        filters={
          <OptionSelect
            aria-label={t("refunds.status")}
            className="md:w-56"
            options={[
              { value: ALL, label: t("refunds.status.all") },
              ...REFUND_STATUSES.map((s) => ({ value: s, label: t(`refunds.status.${s}`) })),
            ]}
            value={status}
            onValueChange={(value) =>
              void navigate({
                search: (prev) => ({ ...prev, status: value as RefundStatus | typeof ALL }),
                replace: true,
              })
            }
          />
        }
        rowActions={(r) =>
          r.status === "requested" && mayApprove
            ? [
                {
                  label: t("refunds.approve"),
                  icon: <BadgeCheckIcon />,
                  onSelect: () => setDeciding({ refund: r, kind: "approve" }),
                },
                {
                  label: t("refunds.decline"),
                  icon: <BanIcon />,
                  onSelect: () => setDeciding({ refund: r, kind: "decline" }),
                },
              ]
            : []
        }
        empty={{ title: t("refunds.emptyTitle"), description: t("refunds.emptyHint") }}
      />
      {creating && order && (
        <RefundDialog order={order} mayApprove={mayApprove} onClose={() => setCreating(false)} />
      )}
      {deciding?.kind === "approve" && (
        <ApproveDialog refund={deciding.refund} onClose={() => setDeciding(null)} />
      )}
      {deciding?.kind === "decline" && (
        <DeclineDialog refund={deciding.refund} onClose={() => setDeciding(null)} />
      )}
    </Page>
  );
}

function OrderCard(props: { order: RefundableOrderView; onRefund: () => void; timeZone: string }) {
  const { order } = props;
  const { t, format } = useRefundMessages();
  const money = (v: string) => format.money(BigInt(v));
  return (
    <Card className="gap-4" data-testid="refund-order">
      <CardHeader>
        <CardTitle className="tabular-nums">{t("refunds.order", { number: order.receiptNumber })}</CardTitle>
        <CardDescription>
          {format.dateTime(order.completedAt, props.timeZone)}
          {order.payment &&
            ` · ${t("refunds.paidWith", {
              method: t(`refunds.paymentMethod.${order.payment.method}`),
              amount: money(order.payment.amount),
            })}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <dl className="grid grid-cols-3 gap-2 rounded-xl bg-muted p-4 text-sm">
          <div>
            <dt className="text-muted-foreground">{t("refunds.captured")}</dt>
            <dd className="font-semibold tabular-nums">{money(order.captured)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">{t("refunds.held")}</dt>
            <dd className="font-semibold tabular-nums">{money(order.held)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">{t("refunds.refundable")}</dt>
            <dd className="font-semibold tabular-nums" data-testid="refund-refundable">
              {money(order.refundable)}
            </dd>
          </div>
        </dl>
        <section className="flex flex-col gap-2">
          <h2 className="font-semibold">{t("refunds.lines")}</h2>
          {/* Not paginated (CODING_STANDARDS §4 exception): an order has at most MAX_ORDER_LINES lines. */}
          <ul className="flex flex-col divide-y text-sm">
            {order.lines.map((line) => (
              <li key={line.orderLineId} className="flex justify-between gap-3 py-2">
                <span className="min-w-0">
                  {line.name}
                  <span className="block text-muted-foreground">
                    {t("refunds.lineSold", { quantity: line.quantity, refunded: line.refundedQuantity })}
                  </span>
                </span>
                <span className="tabular-nums">{money(line.netTotal)}</span>
              </li>
            ))}
          </ul>
        </section>
        <Button
          type="button"
          className="self-start"
          disabled={order.payment === null || BigInt(order.refundable) === 0n}
          onClick={props.onRefund}
        >
          <Undo2Icon aria-hidden />
          {t("refunds.new")}
        </Button>
        <section className="flex flex-col gap-2 md:max-w-md">
          <h2 className="font-semibold">{t("refunds.printTitle")}</h2>
          <p className="text-muted-foreground text-sm">{t("refunds.printHint")}</p>
          <ReceiptActions orderId={order.orderId} />
        </section>
        <section className="flex flex-col gap-2">
          <h2 className="font-semibold">{t("refunds.history")}</h2>
          {order.refunds.length === 0 && <p className="text-muted-foreground text-sm">{t("refunds.none")}</p>}
          {/* Not paginated (CODING_STANDARDS §4 exception): one order's refunds are a handful. */}
          <ul className="flex flex-col gap-2">
            {order.refunds.map((refund) => (
              <li
                key={refund.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border p-3 text-sm"
                data-testid="order-refund"
              >
                <span className="min-w-0">
                  <span className="font-medium tabular-nums">{format.money(BigInt(refund.amount))}</span>
                  {refund.method && ` · ${t(`refunds.method.${refund.method}`)}`}
                  <span className="block text-muted-foreground">{refund.failureReason ?? refund.reason}</span>
                  {refund.status === "unknown" && (
                    <span className="block text-warning">{t("refunds.unknownHint")}</span>
                  )}
                </span>
                <StatusBadge status={refund.status} />
              </li>
            ))}
          </ul>
        </section>
      </CardContent>
    </Card>
  );
}

/** The method a manager pays with, and the transfer's evidence when it is one. */
function MethodFields(props: {
  method: RefundMethod;
  onMethod: (method: RefundMethod) => void;
  gateway: boolean;
  online: boolean;
  reference: string;
  onReference: (value: string) => void;
  note: string;
  onNote: (value: string) => void;
}) {
  const { t } = useRefundMessages();
  const ids = { method: useId(), reference: useId(), note: useId() };
  const methods: RefundMethod[] = [
    "cash",
    "manual_transfer",
    ...(props.gateway && props.online ? (["gateway"] as const) : []),
  ];
  return (
    <>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 font-medium text-sm">{t("refunds.method")}</legend>
        <RadioGroup
          value={props.method}
          onValueChange={(value) => props.onMethod(value as RefundMethod)}
          className="flex flex-col gap-3"
        >
          {methods.map((m) => (
            <div key={m} className="flex items-start gap-3">
              <RadioGroupItem id={`${ids.method}-${m}`} value={m} className="mt-1" />
              <Label htmlFor={`${ids.method}-${m}`} className="flex flex-col items-start gap-0.5 font-normal">
                <span className="font-medium">{t(`refunds.method.${m}`)}</span>
                <span className="text-muted-foreground text-sm">{t(`refunds.methodHint.${m}`)}</span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>
      {props.method === "manual_transfer" && (
        <>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.reference}>{t("refunds.transferReference")}</Label>
            <Input
              id={ids.reference}
              maxLength={100}
              autoComplete="off"
              value={props.reference}
              onChange={(e) => props.onReference(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.note}>{t("refunds.evidenceNote")}</Label>
            <Textarea
              id={ids.note}
              maxLength={500}
              value={props.note}
              onChange={(e) => props.onNote(e.target.value)}
            />
          </div>
        </>
      )}
    </>
  );
}

type LineChoice = { quantity: number; disposition: RefundDisposition; inspection: string };

/** A new refund: lines given back with their stock disposition, amount, reason — and, for a manager, how it's paid. */
function RefundDialog(props: { order: RefundableOrderView; mayApprove: boolean; onClose: () => void }) {
  const { order } = props;
  const { t, format } = useRefundMessages();
  const online = useOnlineStatus();
  const queryClient = useQueryClient();
  const [key] = useState(newId);
  const [choices, setChoices] = useState<Record<string, LineChoice>>({});
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [method, setMethod] = useState<RefundMethod>("cash");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const ids = { amount: useId(), reason: useId(), line: useId() };
  const refundable = BigInt(order.refundable);
  const chosen = order.lines.flatMap((line) => {
    const choice = choices[line.orderLineId];
    return choice && choice.quantity > 0 ? [{ line, choice }] : [];
  });
  const linesValue = chosen.reduce(
    (sum, { line, choice }) => sum + lineValue(BigInt(line.netTotal), line.quantity, choice.quantity),
    0n,
  );
  const value = BigInt(amount || "0");
  const valid =
    value > 0n &&
    value <= refundable &&
    reason.trim().length >= 3 &&
    chosen.every(({ choice }) => choice.disposition !== "restock" || choice.inspection.trim().length >= 3) &&
    (!props.mayApprove || method !== "manual_transfer" || reference.trim().length >= 3);
  const save = useMutation({
    mutationFn: async (): Promise<RefundView> =>
      unwrap(
        await api.v1.orders({ orderId: order.orderId }).refunds.post({
          idempotencyKey: key,
          amount: value.toString(),
          reason: reason.trim(),
          lines: chosen.map(({ line, choice }) => ({
            orderLineId: line.orderLineId,
            quantity: choice.quantity,
            disposition: choice.disposition,
            ...(choice.disposition === "restock" ? { inspectionNote: choice.inspection.trim() } : {}),
          })),
          ...(props.mayApprove
            ? {
                authorize: {
                  method,
                  ...(method === "manual_transfer"
                    ? {
                        transferReference: reference.trim(),
                        ...(note.trim() ? { evidenceNote: note.trim() } : {}),
                      }
                    : {}),
                },
              }
            : {}),
        }),
      ),
    onSuccess: async (refund) => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      toast.success(
        t(
          refund.status === "requested"
            ? "refunds.requested"
            : refund.status === "succeeded"
              ? "refunds.paid"
              : "refunds.sent",
        ),
      );
      props.onClose();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid && !save.isPending) save.mutate();
  }
  const setChoice = (lineId: string, patch: Partial<LineChoice>) =>
    setChoices((all) => ({
      ...all,
      [lineId]: { quantity: 0, disposition: "waste", inspection: "", ...all[lineId], ...patch },
    }));
  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("refunds.dialogTitle", { number: order.receiptNumber })}
      description={t("refunds.dialogHint", { amount: format.money(refundable) })}
      dismissible={!save.isPending}
      footer={
        <Button type="submit" form="new-refund" disabled={!valid || save.isPending || !online}>
          {t(
            save.isPending
              ? "refunds.saving"
              : props.mayApprove
                ? "refunds.submitApprove"
                : "refunds.submitRequest",
          )}
        </Button>
      }
    >
      <form id="new-refund" className="flex flex-col gap-4" onSubmit={submit} noValidate autoComplete="off">
        {/* Not paginated (CODING_STANDARDS §4 exception): an order has at most MAX_ORDER_LINES lines. */}
        {order.lines.map((line) => {
          const left = line.quantity - line.refundedQuantity;
          if (left <= 0) return null;
          const choice = choices[line.orderLineId];
          const lineId = `${ids.line}-${line.orderLineId}`;
          return (
            <fieldset key={line.orderLineId} className="flex flex-col gap-2 rounded-xl border p-3">
              <legend className="px-1 font-medium text-sm">{line.name}</legend>
              <div className="grid grid-cols-[6rem_1fr] gap-2">
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`${lineId}-qty`} className="text-xs">
                    {t("refunds.quantity", { name: line.name })}
                  </Label>
                  <Input
                    id={`${lineId}-qty`}
                    inputMode="numeric"
                    className="tabular-nums"
                    value={String(choice?.quantity ?? 0)}
                    onChange={(e) =>
                      setChoice(line.orderLineId, {
                        quantity: Math.min(left, Number(e.target.value.replace(/\D/g, "") || "0")),
                      })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-muted-foreground text-xs">
                    {t("refunds.disposition", { name: line.name })}
                  </span>
                  <OptionSelect
                    aria-label={t("refunds.disposition", { name: line.name })}
                    options={(
                      [
                        "waste",
                        ...(line.productKind === "retail" ? ["restock"] : []),
                        "none",
                      ] as RefundDisposition[]
                    ).map((d) => ({ value: d, label: t(`refunds.disposition.${d}`) }))}
                    value={choice?.disposition ?? "waste"}
                    onValueChange={(d) =>
                      setChoice(line.orderLineId, { disposition: d as RefundDisposition })
                    }
                  />
                </div>
              </div>
              {choice?.disposition === "restock" && (
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`${lineId}-inspection`} className="text-xs">
                    {t("refunds.inspection", { name: line.name })}
                  </Label>
                  <Input
                    id={`${lineId}-inspection`}
                    maxLength={200}
                    placeholder={t("refunds.inspectionPlaceholder")}
                    value={choice.inspection}
                    onChange={(e) => setChoice(line.orderLineId, { inspection: e.target.value })}
                  />
                </div>
              )}
            </fieldset>
          );
        })}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.amount}>{t("refunds.amount")}</Label>
          <Input
            id={ids.amount}
            inputMode="numeric"
            className="tabular-nums"
            value={amount}
            onChange={(e) =>
              setAmount(
                e.target.value
                  .replace(/\D/g, "")
                  .replace(/^0+(?=\d)/, "")
                  .slice(0, 12),
              )
            }
          />
          <p className="text-muted-foreground text-sm tabular-nums">
            {t("refunds.amountHint", { lines: format.money(linesValue), max: format.money(refundable) })}
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.reason}>{t("refunds.reason")}</Label>
          <Textarea
            id={ids.reason}
            maxLength={200}
            placeholder={t("refunds.reasonPlaceholder")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        {props.mayApprove && (
          <MethodFields
            method={method}
            onMethod={setMethod}
            gateway={order.payment?.gatewayRefund === true}
            online={online}
            reference={reference}
            onReference={setReference}
            note={note}
            onNote={setNote}
          />
        )}
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {failure(t, save.error)}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}

/** A manager authorizes a requested refund with how it's paid. */
function ApproveDialog(props: { refund: RefundView; onClose: () => void }) {
  const { refund } = props;
  const { t, format } = useRefundMessages();
  const online = useOnlineStatus();
  const queryClient = useQueryClient();
  const [method, setMethod] = useState<RefundMethod>("cash");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const valid = method !== "manual_transfer" || reference.trim().length >= 3;
  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.v1.refunds({ refundId: refund.id }).authorize.post({
          method,
          expectedRevision: refund.revision,
          ...(method === "manual_transfer"
            ? { transferReference: reference.trim(), ...(note.trim() ? { evidenceNote: note.trim() } : {}) }
            : {}),
        }),
      ),
    onSuccess: async (approved) => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      toast.success(t(approved.status === "succeeded" ? "refunds.paid" : "refunds.sent"));
      props.onClose();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid && !save.isPending) save.mutate();
  }
  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("refunds.approveTitle", { amount: format.money(BigInt(refund.amount)) })}
      description={refund.reason}
      dismissible={!save.isPending}
      footer={
        <Button type="submit" form="approve-refund" disabled={!valid || save.isPending || !online}>
          {t(save.isPending ? "refunds.saving" : "refunds.submitApprove")}
        </Button>
      }
    >
      <form id="approve-refund" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <MethodFields
          method={method}
          onMethod={setMethod}
          gateway={refund.paymentMethod === "qris"}
          online={online}
          reference={reference}
          onReference={setReference}
          note={note}
          onNote={setNote}
        />
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {failure(t, save.error)}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}

/** A manager declines a requested refund, and says why. */
function DeclineDialog(props: { refund: RefundView; onClose: () => void }) {
  const { refund } = props;
  const { t, format } = useRefundMessages();
  const queryClient = useQueryClient();
  const reasonId = useId();
  const [reason, setReason] = useState("");
  const valid = reason.trim().length >= 3;
  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.v1.refunds({ refundId: refund.id }).decline.post({
          reason: reason.trim(),
          expectedRevision: refund.revision,
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      toast.success(t("refunds.declined"));
      props.onClose();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid && !save.isPending) save.mutate();
  }
  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("refunds.declineTitle")}
      description={`${format.money(BigInt(refund.amount))} · ${refund.reason}`}
      dismissible={!save.isPending}
      footer={
        <Button type="submit" variant="destructive" form="decline-refund" disabled={!valid || save.isPending}>
          {t(save.isPending ? "refunds.saving" : "refunds.decline")}
        </Button>
      }
    >
      <form id="decline-refund" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={reasonId}>{t("refunds.declineReason")}</Label>
          <Textarea
            id={reasonId}
            maxLength={200}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {failure(t, save.error)}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}
